import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Install LAMP Stack',
    description: 'Install and configure the Linux, Apache, MySQL, and PHP stack required for WordPress.',
    requirements: [
      {
        text: 'Update the system package repositories'
      },
      {
        text: 'Install the following components:',
        subitems: ['Apache web server', 'MySQL database server', 'PHP and required PHP modules', 'Apache PHP module']
      },
      {
        text: 'Start and enable Apache service'
      },
      {
        text: 'Start and enable MySQL service'
      },
      {
        text: 'Verify PHP is working with Apache'
      }
    ],
    verification: [
      'Apache service is running and enabled',
      'MySQL service is running and enabled', 
      'PHP is installed with Apache module',
      'All required PHP extensions are installed',
      'Services are configured to start on boot'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Create WordPress Database',
    description: 'Set up a dedicated MySQL database and user for WordPress with appropriate privileges.',
    requirements: [
      {
        text: 'Log into MySQL as root user'
      },
      {
        text: 'Create a new database named wordpress_db'
      },
      {
        text: 'Create a dedicated WordPress user:',
        subitems: ['Username: wp_user', 'Strong password required', 'Host: localhost']
      },
      {
        text: 'Grant all privileges on wordpress_db to wp_user'
      },
      {
        text: 'Flush privileges to apply changes'
      }
    ],
    verification: [
      'Database wordpress_db exists in MySQL',
      'User wp_user can connect with credentials',
      'wp_user has full privileges on wordpress_db',
      'No errors when testing database connection',
      'MySQL access is properly secured'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Download and Install WordPress',
    description: 'Download the latest WordPress release and set it up in the appropriate web directory with correct permissions.',
    requirements: [
      {
        text: 'Download the latest WordPress archive from wordpress.org'
      },
      {
        text: 'Extract WordPress to /var/www/html/wordpress'
      },
      {
        text: 'Set proper ownership and permissions:',
        subitems: ['Owner: www-data user and group', 'Directories: 755 permissions', 'Files: 644 permissions']
      },
      {
        text: 'Copy wp-config-sample.php to wp-config.php'
      },
      {
        text: 'Configure database connection in wp-config.php'
      },
      {
        text: 'Set WordPress authentication salts to non-placeholder values'
      }
    ],
    verification: [
      'WordPress files exist in /var/www/html/wordpress',
      'All files owned by www-data:www-data',
      'wp-config.php contains correct database settings',
      'Authentication keys are configured',
      'File permissions allow WordPress to function'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Configure Apache for WordPress',
    description: 'Set up Apache virtual host and modules to properly serve the WordPress application.',
    requirements: [
      {
        text: 'Create a virtual host configuration for WordPress'
      },
      {
        text: 'Configure the following Apache settings:',
        subitems: ['DocumentRoot points to WordPress directory', 'ServerName configured appropriately', 'Directory permissions allow .htaccess overrides']
      },
      {
        text: 'Enable required Apache modules:',
        subitems: ['mod_rewrite for permalinks', 'mod_headers for security headers', 'mod_expires for caching']
      },
      {
        text: 'Enable the WordPress site configuration'
      },
      {
        text: 'Required paths (for grading):',
        subitems: [
          '/etc/apache2/sites-available/wordpress.conf (site config file)',
          '/etc/apache2/sites-enabled/wordpress.conf (enabled symlink)',
          'DocumentRoot points to /var/www/html/wordpress',
          'AllowOverride All is set for /var/www/html/wordpress'
        ]
      },
      {
        text: 'Reload Apache to apply changes'
      }
    ],
    verification: [
      'Virtual host configuration is active',
      'mod_rewrite is enabled and working',
      '.htaccess files are processed correctly',
      'WordPress is accessible via web browser',
      'Apache serves WordPress without errors'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Complete WordPress Setup',
    description: 'Finish the WordPress installation through the web interface and perform initial configuration.',
    requirements: [
      {
        text: 'Navigate to http://localhost/ in a web browser'
      },
      {
        text: 'Complete the installation wizard:',
        subitems: ['Set site title and tagline', 'Create admin user with strong password', 'Configure admin email address', 'Set search engine visibility preference']
      },
      {
        text: 'Log into WordPress admin dashboard'
      },
      {
        text: 'Configure permalink structure for SEO-friendly URLs'
      },
      {
        text: 'Verify the site is fully functional'
      }
    ],
    verification: [
      'WordPress installation wizard completes successfully',
      'Admin can log into WordPress dashboard',
      'Homepage displays without errors',
      'WordPress database tables exist, including wp_options and wp_users',
      'Permalinks work correctly',
      'Sample post and page are accessible'
    ]
  }
];

const WordPressBasicsInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WordPressBasicsInstructions;
